import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Bookmark } from 'lucide-react'
import { CardContent, CardIcon } from '@/components/ui/card'
import { ListCard } from '@/components/ListCard'
import { ListCardHeader } from '@/components/ListCardHeader'
import { LineItemTable } from '@/components/LineItemTable'
import { LineItemFilters, useLineItemFilters } from '@/components/LineItemFilters'
import { SearchInput } from '@/components/SearchInput'
import { useNetwork } from '../contexts/NetworkContext'
import { useTaggedLineItems } from '@/hooks/useTaggedLineItems'
import { getContext } from '@/services/networks/networksApi'
import { LineItemsApi } from '@/services/lineItems/lineItemsApi'
import { filterBySearch } from '@/utils/search'

/** The line items the user has tagged, most recently tagged first. */
export function TaggedLineItemsCard() {
  const { t } = useTranslation()
  const { scopeKey, scopeReady } = useNetwork()
  const context = getContext()
  const { ids, isLoading: idsLoading } = useTaggedLineItems()
  const { filters, setFilters, applyFilters, defaults } = useLineItemFilters(
    { objectState: 'all' },
    'taggedLineItems'
  )
  const [search, setSearch] = useState('')

  // Starts with 'lineitems' so a save on the line item page refreshes it too.
  const { data = [], isLoading } = useQuery({
    queryKey: ['lineitems', 'tagged', ids, context, scopeKey],
    queryFn: () => LineItemsApi.getByIds(ids, { withTargeting: true }),
    enabled: scopeReady && ids.length > 0,
  })

  // The API returns them in its own order; keep the order they were tagged in.
  const rows = useMemo(() => {
    const byId = new Map(data.map((li) => [li.id, li]))
    return ids.flatMap((id) => byId.get(id) ?? [])
  }, [data, ids])

  return (
    <ListCard>
      <ListCardHeader
        title={
          <>
            <CardIcon icon={Bookmark} />
            {t('advertisingHome.tagged')}
          </>
        }
        count={rows.length}
        controls={
          <>
            <SearchInput value={search} onChange={setSearch} />
            <LineItemFilters filters={filters} onChange={setFilters} defaults={defaults} />
          </>
        }
      />
      <CardContent className="p-0">
        <LineItemTable
          lineItems={filterBySearch(applyFilters(rows), search)}
          loading={idsLoading || (ids.length > 0 && isLoading)}
          emptyMessage={ids.length === 0 ? t('advertisingHome.taggedEmpty') : undefined}
        />
      </CardContent>
    </ListCard>
  )
}
